import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import api from "../../api/Api";
import {
  Search,
  RotateCcw,
  Star,
  ChevronRight,
  ChevronDown,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Users,
  Phone,
  Mail,
  Building2,
  Calendar,
} from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";

const EMPTY_FILTERS = { search: "", from: "", to: "", rating: "" };
const PAGE_SIZE = 20;

// ---------- look & feel helpers (no effect on data or API calls) ----------
const AVATAR_COLORS = [
  { bg: "#EEF2FF", text: "#4F46E5" },
  { bg: "#ECFDF5", text: "#059669" },
  { bg: "#FFF7ED", text: "#EA580C" },
  { bg: "#FDF2F8", text: "#DB2777" },
  { bg: "#F0F9FF", text: "#0284C7" },
  { bg: "#FAF5FF", text: "#7C3AED" },
];

const TAG_COLORS = [
  { bg: "#EFF6FF", text: "#2563EB", border: "#BFDBFE" },
  { bg: "#F0FDF4", text: "#16A34A", border: "#BBF7D0" },
  { bg: "#FFF7ED", text: "#EA580C", border: "#FED7AA" },
  { bg: "#FAF5FF", text: "#7C3AED", border: "#E9D5FF" },
  { bg: "#FDF2F8", text: "#C026D3", border: "#FBCFE8" },
  { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA" },
];

const RATING_TONES = {
  good: { bg: "#DCFCE7", text: "#15803D", border: "#BBF7D0", accent: "#22C55E" },
  mid: { bg: "#FEF3C7", text: "#B45309", border: "#FDE68A", accent: "#F59E0B" },
  low: { bg: "#FEE2E2", text: "#B91C1C", border: "#FECACA", accent: "#EF4444" },
  none: { bg: "#F1F5F9", text: "#64748B", border: "#E2E8F0", accent: "#CBD5E1" },
};

const toneFor = (value) => {
  if (value === null || value === undefined) return RATING_TONES.none;
  if (value >= 4) return RATING_TONES.good;
  if (value >= 3) return RATING_TONES.mid;
  return RATING_TONES.low;
};

const initialsOf = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "?";

const avatarFor = (name) => {
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d)) return "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Stars = ({ value, size = 14 }) => (
  <span className="d-inline-flex align-items-center gap-1" aria-label={`${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={size}
        color={n <= value ? "#F59E0B" : "#E2E8F0"}
        fill={n <= value ? "#F59E0B" : "none"}
      />
    ))}
  </span>
);

const FeedbackAgentWisePage = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const appliedRef = useRef(EMPTY_FILTERS); // filters used for the data on screen

  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState({ total_feedbacks: 0, avg_rating: null, total_agents: 0 });
  const [loading, setLoading] = useState(true);

  const [openAgentId, setOpenAgentId] = useState(null);
  // { [agentId]: { items: [], total: 0, page: 0, loading: false } }
  const [details, setDetails] = useState({});

  const summaryRequest = useRef(0);

  const cleanParams = (f) =>
    Object.fromEntries(Object.entries(f).filter(([, v]) => String(v).trim() !== ""));

  const loadSummary = useCallback(async (f) => {
    const requestNo = ++summaryRequest.current;
    setLoading(true);
    try {
      const res = await api.get("/agentfeedback/feedback-agent-summary", { params: cleanParams(f) });
      if (requestNo !== summaryRequest.current) return; // a newer search replaced this one
      if (res.data.success) {
        appliedRef.current = f;
        setAgents(res.data.data);
        setStats(res.data.stats);
        setOpenAgentId(null);
        setDetails({});
      } else {
        toast.error(res.data.message || "Could not load feedback");
      }
    } catch (error) {
      if (requestNo === summaryRequest.current) toast.error("Server error");
    } finally {
      if (requestNo === summaryRequest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary(EMPTY_FILTERS);
  }, [loadSummary]);

  const loadDetails = async (agentId, page) => {
    setDetails((prev) => ({
      ...prev,
      [agentId]: { items: [], total: 0, page: 0, ...prev[agentId], loading: true },
    }));
    try {
      const res = await api.get(`/agentfeedback/feedback-agent/${agentId}`, {
        params: { ...cleanParams(appliedRef.current), page, limit: PAGE_SIZE },
      });
      if (res.data.success) {
        setDetails((prev) => {
          const old = prev[agentId] || { items: [] };
          return {
            ...prev,
            [agentId]: {
              items: page === 1 ? res.data.data : [...old.items, ...res.data.data],
              total: res.data.total,
              page,
              loading: false,
            },
          };
        });
      } else {
        throw new Error(res.data.message);
      }
    } catch (error) {
      toast.error("Could not load this agent's feedback");
      setDetails((prev) => ({
        ...prev,
        [agentId]: { items: [], total: 0, page: 0, ...prev[agentId], loading: false },
      }));
    }
  };

  const toggleAgent = (agentId) => {
    if (openAgentId === agentId) {
      setOpenAgentId(null);
      return;
    }
    setOpenAgentId(agentId);
    if (!details[agentId] || details[agentId].page === 0) loadDetails(agentId, 1);
  };

  const applyFilters = () => {
    if (filters.from && filters.to && filters.from > filters.to) {
      toast.error("'From' date must be on or before 'To' date");
      return;
    }
    loadSummary(filters);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    loadSummary(EMPTY_FILTERS);
  };

  const setField = (name) => (e) => setFilters((prev) => ({ ...prev, [name]: e.target.value }));

  // Derived from the rows already returned by the summary API
  const recommendedTotal = agents.reduce((sum, a) => sum + (a.recommended || 0), 0);
  const avgTone = toneFor(stats.avg_rating);

  const statCards = [
    {
      label: "TOTAL FEEDBACKS",
      value: stats.total_feedbacks,
      icon: <MessageSquare size={20} color="#F8FAFC" />,
      bg: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
    },
    {
      label: "AGENTS RATED",
      value: stats.total_agents,
      icon: <Users size={20} color="#FFFFFF" />,
      bg: "linear-gradient(135deg, #6366F1 0%, #4338CA 100%)",
    },
    {
      label: "AVERAGE RATING",
      value: stats.avg_rating === null ? "-" : Number(stats.avg_rating).toFixed(1),
      suffix: <Star size={18} color="#FEF3C7" fill="#FEF3C7" />,
      icon: <Star size={20} color="#FFFFFF" />,
      bg: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
    },
    {
      label: "RECOMMENDED",
      value: recommendedTotal,
      icon: <ThumbsUp size={20} color="#FFFFFF" />,
      bg: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    },
  ];

  return (
    <div
      className="agw p-2 w-100"
      style={{ backgroundColor: "#F4F7FE", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}
    >
      <style>{`
        .agw .stat-card { border-radius: 20px; border: none; box-shadow: 0 4px 15px rgba(0,0,0,0.04); transition: transform .2s; }
        .agw .stat-card:hover { transform: translateY(-2px); }
        .agw .panel { background: #fff; border: none; border-radius: 20px; box-shadow: 0 4px 15px rgba(15,23,42,0.04); }
        .agw .field-label { font-size: .72rem; font-weight: 700; color: #64748B; margin-bottom: 4px; }
        .agw .field-input { border: 1px solid #E2E8F0; border-radius: 12px; font-size: .85rem; padding: 8px 12px; background: #F8FAFC; transition: all .2s; }
        .agw .field-input:focus { background: #fff; border-color: #818CF8; box-shadow: 0 0 0 3px rgba(99,102,241,.15); outline: none; }
        .agw .btn-search { background: linear-gradient(135deg, #6366F1 0%, #4338CA 100%); color: #fff; border: none; border-radius: 12px; font-weight: 600; font-size: .85rem; padding: 9px 16px; }
        .agw .btn-search:hover:not(:disabled) { filter: brightness(1.08); color: #fff; }
        .agw .btn-reset { background: #fff; color: #475569; border: 1px solid #E2E8F0; border-radius: 12px; padding: 9px 12px; }
        .agw .btn-reset:hover:not(:disabled) { background: #F8FAFC; }

        .agw .table-wrapper { max-height: calc(100vh - 430px); min-height: 260px; overflow: auto; }
        .agw .table-wrapper::-webkit-scrollbar { width: 6px; height: 6px; }
        .agw .table-wrapper::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 10px; }
        .agw .agent-table th { font-size: .72rem; letter-spacing: .5px; text-transform: uppercase; color: #64748B; padding: 12px 16px; background: #fff; position: sticky; top: 0; z-index: 5; box-shadow: 0 1px 0 #F1F5F9; white-space: nowrap; }
        .agw .agent-table td { padding: 14px 16px; vertical-align: middle; border-bottom: 1px solid #F1F5F9; }
        .agw .agent-row { cursor: pointer; transition: background .15s; }
        .agw .agent-row:hover { background: #F8FAFF; }
        .agw .agent-row.open { background: #EEF2FF; }
        .agw .agent-row:focus-visible { outline: 2px solid #6366F1; outline-offset: -2px; }

        .agw .avatar { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: .8rem; flex-shrink: 0; }
        .agw .pill { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 100px; font-size: .75rem; font-weight: 700; border: 1px solid transparent; }
        .agw .count-chip { display: inline-flex; align-items: center; justify-content: center; min-width: 32px; height: 28px; padding: 0 10px; border-radius: 100px; background: #EEF2FF; color: #4338CA; font-weight: 700; font-size: .8rem; }
        .agw .chevron { width: 28px; height: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; background: #F1F5F9; color: #64748B; transition: all .2s; }
        .agw .agent-row.open .chevron { background: #6366F1; color: #fff; }

        .agw .detail-cell { background: linear-gradient(180deg, #F8FAFF 0%, #F1F5FF 100%); padding: 16px 20px !important; }
        .agw .detail-card { background: #fff; border-radius: 14px; border: 1px solid #E2E8F0; overflow: hidden; }
        .agw .detail-table th { font-size: .7rem; letter-spacing: .4px; text-transform: uppercase; color: #64748B; background: #F8FAFC; padding: 10px 14px; white-space: nowrap; }
        .agw .detail-table td { padding: 12px 14px; vertical-align: top; border-bottom: 1px solid #F1F5F9; }
        .agw .detail-table tr:last-child td { border-bottom: none; }
        .agw .btn-more { background: #fff; color: #4338CA; border: 1px solid #C7D2FE; border-radius: 10px; font-size: .8rem; font-weight: 600; padding: 6px 14px; }
        .agw .btn-more:hover:not(:disabled) { background: #EEF2FF; }
      `}</style>

      {/* HEADER */}
      <div className="mb-4">
        <h4 className="fw-bold mb-1" style={{ color: "#0F172A", letterSpacing: "-0.5px" }}>
          Agent Feedbacks
        </h4>
        <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
          See what each agent rated after their trips ended.
        </p>
      </div>

      {/* STAT CARDS */}
      <div className="row g-3 mb-4">
        {statCards.map((c) => (
          <div className="col-12 col-sm-6 col-xl-3" key={c.label}>
            <div className="card stat-card h-100" style={{ background: c.bg, color: "white" }}>
              <div className="card-body p-3 px-4 d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-white-50 fw-bold mb-1" style={{ fontSize: "0.7rem", letterSpacing: "1px" }}>
                    {c.label}
                  </h6>
                  <h3 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    {c.value} {c.suffix}
                  </h3>
                </div>
                <div
                  className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 48, height: 48 }}
                >
                  {c.icon}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* FILTERS */}
      <div className="panel mb-4">
        <div className="p-3 px-4">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-4">
              <div className="field-label">Search agent</div>
              <div className="position-relative">
                <Search
                  size={15}
                  color="#94A3B8"
                  style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
                />
                <input
                  type="text"
                  className="form-control field-input"
                  style={{ paddingLeft: 36 }}
                  placeholder="Name, agent ID, phone or company"
                  value={filters.search}
                  onChange={setField("search")}
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                  maxLength={60}
                />
              </div>
            </div>
            <div className="col-6 col-md-2">
              <div className="field-label">From</div>
              <input type="date" className="form-control field-input" value={filters.from} onChange={setField("from")} />
            </div>
            <div className="col-6 col-md-2">
              <div className="field-label">To</div>
              <input type="date" className="form-control field-input" value={filters.to} onChange={setField("to")} />
            </div>
            <div className="col-6 col-md-2">
              <div className="field-label">Rating</div>
              <select className="form-select field-input" value={filters.rating} onChange={setField("rating")}>
                <option value="">All ratings</option>
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? "star" : "stars"}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2 d-flex gap-2">
              <button
                className="btn btn-search d-flex align-items-center justify-content-center gap-2 flex-grow-1"
                onClick={applyFilters}
                disabled={loading}
              >
                <Search size={15} /> Search
              </button>
              <button
                className="btn btn-reset d-flex align-items-center justify-content-center"
                onClick={resetFilters}
                disabled={loading}
                title="Reset filters"
              >
                <RotateCcw size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* AGENT TABLE */}
      <div className="panel overflow-hidden">
        <div className="d-flex align-items-center justify-content-between px-4 pt-3 pb-2">
          <span className="fw-bold" style={{ color: "#0F172A", fontSize: "0.95rem" }}>
            Agents
          </span>
          <span className="pill" style={{ background: "#EEF2FF", color: "#4338CA", borderColor: "#C7D2FE" }}>
            {agents.length} {agents.length === 1 ? "agent" : "agents"}
          </span>
        </div>

        <div className="table-responsive table-wrapper">
          <table className="table agent-table mb-0">
            <thead>
              <tr>
                <th style={{ width: 56 }}></th>
                <th>Agent</th>
                <th>Contact</th>
                <th className="text-center">Feedbacks</th>
                <th className="text-center">Avg rating</th>
                <th className="text-center">Low (1-2 stars)</th>
                <th>Last feedback</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="text-center py-5">
                    <div className="spinner-border spinner-border-sm" style={{ color: "#6366F1" }} role="status"></div>
                    <div className="text-muted small mt-2">Loading feedbacks...</div>
                  </td>
                </tr>
              )}

              {!loading && agents.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-5 fw-medium">
                    No feedback found for these filters.
                  </td>
                </tr>
              )}

              {!loading &&
                agents.map((a) => {
                  const isOpen = openAgentId === a.agent_id;
                  const d = details[a.agent_id];
                  const av = avatarFor(a.agent_name);
                  const tone = toneFor(a.avg_rating);
                  return (
                    <Fragment key={a.agent_id}>
                      <tr
                        className={`agent-row ${isOpen ? "open" : ""}`}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isOpen}
                        onClick={() => toggleAgent(a.agent_id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleAgent(a.agent_id);
                          }
                        }}
                      >
                        <td className="text-center">
                          <span className="chevron">
                            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <div className="avatar" style={{ background: av.bg, color: av.text }}>
                              {initialsOf(a.agent_name)}
                            </div>
                            <div>
                              <div className="fw-bold" style={{ color: "#0F172A", fontSize: "0.9rem" }}>
                                {a.agent_name}
                              </div>
                              <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
                                {a.agent_code && (
                                  <span className="pill" style={{ background: "#F1F5F9", color: "#475569", padding: "2px 8px", fontSize: "0.68rem" }}>
                                    {a.agent_code}
                                  </span>
                                )}
                                {a.company_name && (
                                  <span className="text-muted d-inline-flex align-items-center gap-1" style={{ fontSize: "0.75rem" }}>
                                    <Building2 size={12} /> {a.company_name}
                                  </span>
                                )}
                                {!a.agent_code && !a.company_name && <span className="text-muted small">-</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ fontSize: "0.82rem" }}>
                          <div className="d-flex align-items-center gap-2" style={{ color: "#1E293B", fontWeight: 600 }}>
                            <Phone size={13} color="#3B82F6" /> {a.phone || "-"}
                          </div>
                          {a.email && (
                            <div className="d-flex align-items-center gap-2 text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                              <Mail size={13} color="#94A3B8" /> {a.email}
                            </div>
                          )}
                        </td>
                        <td className="text-center">
                          <span className="count-chip">{a.total_feedbacks}</span>
                        </td>
                        <td className="text-center">
                          {a.avg_rating === null ? (
                            "-"
                          ) : (
                            <span
                              className="pill"
                              style={{ background: tone.bg, color: tone.text, borderColor: tone.border }}
                            >
                              <Star size={12} fill={tone.accent} color={tone.accent} /> {a.avg_rating.toFixed(1)} / 5
                            </span>
                          )}
                        </td>
                        <td className="text-center">
                          {a.low_ratings > 0 ? (
                            <span
                              className="pill"
                              style={{ background: RATING_TONES.low.bg, color: RATING_TONES.low.text, borderColor: RATING_TONES.low.border }}
                            >
                              {a.low_ratings}
                            </span>
                          ) : (
                            <span className="text-muted">0</span>
                          )}
                        </td>
                        <td>
                          <span className="d-inline-flex align-items-center gap-2 text-muted" style={{ fontSize: "0.78rem" }}>
                            <Calendar size={13} /> {formatDateTime(a.last_feedback_at)}
                          </span>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr>
                          <td
                            colSpan={7}
                            className="detail-cell"
                            style={{ borderLeft: `4px solid ${tone.accent}` }}
                          >
                            {(!d || (d.loading && d.items.length === 0)) && (
                              <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm" style={{ color: "#6366F1" }} role="status"></div>
                              </div>
                            )}

                            {d && !d.loading && d.items.length === 0 && (
                              <div className="text-center text-muted py-3">No feedback to show.</div>
                            )}

                            {d && d.items.length > 0 && (
                              <div className="detail-card">
                                <div className="table-responsive">
                                  <table className="table detail-table mb-0">
                                    <thead>
                                      <tr>
                                        <th>Trip</th>
                                        <th>Rating</th>
                                        <th>Tags</th>
                                        <th>Comment</th>
                                        <th className="text-center">Recommends</th>
                                        <th>Submitted</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {d.items.map((f) => (
                                        <tr key={f.id}>
                                          <td style={{ fontSize: "0.82rem" }}>
                                            <div className="fw-bold" style={{ color: "#0F172A" }}>
                                              {f.booking_ref || f.trip_id}
                                            </div>
                                            <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                                              Enquiry #{f.enq_id}
                                            </div>
                                          </td>
                                          <td style={{ whiteSpace: "nowrap" }}>
                                            <Stars value={f.rating} />
                                          </td>
                                          <td style={{ minWidth: 170 }}>
                                            <div className="d-flex flex-wrap gap-1">
                                              {f.tags.length === 0 && (
                                                <span className="text-muted fst-italic" style={{ fontSize: "0.72rem" }}>
                                                  No tags provided
                                                </span>
                                              )}
                                              {f.tags.map((t, i) => {
                                                const c = TAG_COLORS[i % TAG_COLORS.length];
                                                return (
                                                  <span
                                                    key={t}
                                                    className="badge fw-semibold rounded-pill"
                                                    style={{
                                                      fontSize: "0.64rem",
                                                      padding: "4px 9px",
                                                      backgroundColor: c.bg,
                                                      color: c.text,
                                                      border: `1px solid ${c.border}`,
                                                    }}
                                                  >
                                                    {t}
                                                  </span>
                                                );
                                              })}
                                            </div>
                                          </td>
                                          <td style={{ minWidth: 200, maxWidth: 360, fontSize: "0.82rem" }}>
                                            {f.comment ? (
                                              <span className="d-inline-flex align-items-start gap-2 text-muted">
                                                <MessageSquare size={13} color="#94A3B8" className="mt-1 flex-shrink-0" />
                                                <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.45 }}>
                                                  {f.comment}
                                                </span>
                                              </span>
                                            ) : (
                                              <span className="fst-italic opacity-50 text-muted">No remarks</span>
                                            )}
                                          </td>
                                          <td className="text-center">
                                            <span
                                              className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                              style={{
                                                width: 28,
                                                height: 28,
                                                background: f.recommend_driver ? "#DCFCE7" : "#FEE2E2",
                                              }}
                                            >
                                              {f.recommend_driver ? (
                                                <ThumbsUp size={14} color="#15803D" />
                                              ) : (
                                                <ThumbsDown size={14} color="#B91C1C" />
                                              )}
                                            </span>
                                          </td>
                                          <td className="text-muted" style={{ whiteSpace: "nowrap", fontSize: "0.76rem" }}>
                                            {formatDateTime(f.created_at)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {d && d.items.length > 0 && (
                              <div className="d-flex align-items-center justify-content-between mt-3">
                                <span className="text-muted fw-medium" style={{ fontSize: "0.78rem" }}>
                                  Showing {d.items.length} of {d.total}
                                </span>
                                {d.items.length < d.total && (
                                  <button
                                    className="btn btn-more"
                                    disabled={d.loading}
                                    onClick={() => loadDetails(a.agent_id, d.page + 1)}
                                  >
                                    {d.loading ? "Loading..." : "Load more"}
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FeedbackAgentWisePage;
